import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';
import React, { useState } from 'react';
import logoSrc from '../../../assets/images/logo_tassomutuo_dark.webp';

interface Props {
  from?: 'dashboard' | 'deep-dive' | 'fatturato-perso' | 'dashboard-admin';
}

const useDashboardPdf = ({ from }: Props) => {
  const [isPrinting, setIsPrinting] = useState(false);

  const getReportTitle = (reportType?: string) => {
    switch (reportType) {
      case 'dashboard':
        return 'Report Dashboard';
      case 'deep-dive':
        return 'Report Deep Dive';
      case 'fatturato-perso':
        return 'Report Fatturato Perso';
      case 'dashboard-admin':
        return 'Report Dashboard Admin';
      default:
        return 'Report';
    }
  };

  const addHeaderFooter = (
    pdf: jsPDF,
    pageNumber: number,
    totalPages: number,
    logoBase64: string,
    filterText?: string
  ) => {
    const pageWidth = pdf.internal.pageSize.getWidth();
    const pageHeight = pdf.internal.pageSize.getHeight();

    // Header
    // Logo (dimensioni ridotte per header)
    pdf.addImage(logoBase64, 'PNG', 10, 5, 40, 12);

    // Titolo report
    pdf.setFontSize(12);
    pdf.setFont('helvetica', 'bold');
    pdf.text(getReportTitle(from), pageWidth / 2, 10, { align: 'center' });

    // Filtri applicati o data generazione
    pdf.setFontSize(8);
    pdf.setFont('helvetica', 'normal');
    const today = new Date().toLocaleDateString('it-IT', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
    if (filterText) {
      pdf.text(filterText, pageWidth - 10, 18, { align: 'right' });
    } else {
      pdf.text(`Data: ${today}`, pageWidth - 10, 10, { align: 'right' });
    }

    // Linea separatrice
    pdf.setDrawColor(0, 51, 153); // Blu Euroansa
    pdf.setLineWidth(0.5);
    pdf.line(10, 20, pageWidth - 10, 20);

    // Footer
    // Linea separatrice
    pdf.line(10, pageHeight - 15, pageWidth - 10, pageHeight - 15);

    // Numero pagina
    pdf.setFontSize(8);
    pdf.text(
      `Pagina ${pageNumber} di ${totalPages}`,
      pageWidth / 2,
      pageHeight - 10,
      { align: 'center' }
    );

    // Nome azienda
    pdf.text('Euroansa SPA', 10, pageHeight - 10);
    pdf.text(`Generato il ${today}`, pageWidth - 10, pageHeight - 10, {
      align: 'right',
    });
  };

  const loadImageAsBase64 = (src: string): Promise<string> => {
    return new Promise((resolve, reject) => {
      const img = new Image();
      img.crossOrigin = 'anonymous';
      img.onload = () => {
        const canvas = document.createElement('canvas');
        canvas.width = img.width;
        canvas.height = img.height;
        const ctx = canvas.getContext('2d');
        ctx?.drawImage(img, 0, 0);
        resolve(canvas.toDataURL('image/png'));
      };
      img.onerror = reject;
      img.src = src;
    });
  };

  const generatePdf = async (
    action: 'download' | 'print',
    contentPrintableRef: React.RefObject<HTMLDivElement>,
    filterText?: string
  ) => {
    const element = contentPrintableRef.current;

    if (!element) {
      console.error('Elemento non trovato per la generazione PDF');
      return;
    }

    setIsPrinting(true);

    // Attendi un breve momento per il rendering completo dei grafici
    await new Promise((resolve) => setTimeout(resolve, 1500));

    try {
      // Carica il logo come base64
      const logoBase64 = await loadImageAsBase64(logoSrc);
      const clone = element.cloneNode(true) as HTMLElement;
      clone.style.position = 'absolute';
      clone.style.left = '-9999px';
      clone.style.width = '310mm';
      clone.style.height = 'auto';
      document.body.appendChild(clone);

      await new Promise((resolve) => setTimeout(resolve, 500));

      // Trova tutti gli elementi con page-break (style inline o data attribute)
      const pageBreakElements = clone.querySelectorAll(
        '[style*="page-break-after"], [style*="break-after"], [data-page-break="after"]'
      );

      // Se non ci sono page break, usa il metodo originale
      if (pageBreakElements.length === 0) {
        const canvas = await html2canvas(clone, {
          scale: 1.5,
          width: clone.scrollWidth,
          height: clone.scrollHeight,
          useCORS: true,
          allowTaint: true,
          logging: false,
          windowWidth: 1920,
          backgroundColor: '#ffffff',
        });

        document.body.removeChild(clone);

        const imgData = canvas.toDataURL('image/jpeg', 0.8);

        const pdf = new jsPDF({
          orientation: 'p',
          unit: 'mm',
          format: 'a4',
          compress: true,
        });

        const pdfPageWidth = pdf.internal.pageSize.getWidth();
        const pdfPageHeight = pdf.internal.pageSize.getHeight();
        const margin = 5;
        const headerHeight = 25; // Spazio per header
        const footerHeight = 20; // Spazio per footer
        const contentWidth = pdfPageWidth - margin * 2;
        const contentHeight =
          pdfPageHeight - margin * 2 - headerHeight - footerHeight;

        const imgProps = pdf.getImageProperties(imgData);
        const imgAspectRatio = imgProps.width / imgProps.height;
        const pdfImgWidth = contentWidth;
        const pdfImgHeight = pdfImgWidth / imgAspectRatio;

        let heightLeft = pdfImgHeight;
        let position = 0;
        let pageCount = 1;

        // Calcola numero totale di pagine
        const totalPages = Math.ceil(pdfImgHeight / contentHeight);

        // Aggiungi header e footer alla prima pagina
        addHeaderFooter(pdf, pageCount, totalPages, logoBase64, filterText);

        pdf.addImage(
          imgData,
          'JPEG',
          margin,
          margin + headerHeight,
          pdfImgWidth,
          pdfImgHeight
        );
        heightLeft -= contentHeight;

        while (heightLeft > 0) {
          position = heightLeft - pdfImgHeight + margin + headerHeight;
          pdf.addPage();
          pageCount++;

          // Aggiungi header e footer a ogni pagina
          addHeaderFooter(pdf, pageCount, totalPages, logoBase64, filterText);

          pdf.addImage(
            imgData,
            'JPEG',
            margin,
            position,
            pdfImgWidth,
            pdfImgHeight
          );
          heightLeft -= contentHeight;
        }

        if (action === 'download') {
          const fileName = `${from}_${
            new Date().toISOString().split('T')[0]
          }.pdf`;
          pdf.save(fileName);
        } else if (action === 'print') {
          pdf.output('dataurlnewwindow');
        }

        return;
      }

      // Suddividi in sezioni basate sui page break
      const sections: HTMLElement[] = [];
      let currentSection = document.createElement('div');
      currentSection.style.width = '310mm';

      Array.from(clone.children).forEach((child) => {
        const childClone = child.cloneNode(true) as HTMLElement;
        currentSection.appendChild(childClone);

        const hasPageBreak =
          child.getAttribute('style')?.includes('page-break-after') ||
          child.getAttribute('style')?.includes('break-after') ||
          child.getAttribute('data-page-break') === 'after';

        if (hasPageBreak) {
          sections.push(currentSection);
          currentSection = document.createElement('div');
          currentSection.style.width = '310mm';
        }
      });

      // Aggiungi l'ultima sezione se non vuota
      if (currentSection.children.length > 0) {
        sections.push(currentSection);
      }

      document.body.removeChild(clone);

      // Genera PDF con sezioni separate
      const pdf = new jsPDF({
        orientation: 'p',
        unit: 'mm',
        format: 'a4',
        compress: true,
      });

      const pdfPageWidth = pdf.internal.pageSize.getWidth();
      const pdfPageHeight = pdf.internal.pageSize.getHeight();
      const margin = 10;
      const headerHeight = 15;
      const footerHeight = 15;
      const contentWidth = pdfPageWidth - margin * 2;

      let globalPageCount = 0;
      let totalPagesCount = 0;

      // Calcola il numero totale di pagine
      for (let i = 0; i < sections.length; i++) {
        const section = sections[i];
        section.style.position = 'absolute';
        section.style.left = '-9999px';
        document.body.appendChild(section);
        await new Promise((resolve) => setTimeout(resolve, 100));

        const canvas = await html2canvas(section, {
          scale: 1.5,
          width: section.scrollWidth,
          height: section.scrollHeight,
          useCORS: true,
          allowTaint: true,
          logging: false,
          windowWidth: 1920,
          backgroundColor: '#ffffff',
        });

        document.body.removeChild(section);

        const imgProps = pdf.getImageProperties(
          canvas.toDataURL('image/jpeg', 0.8)
        );
        const imgAspectRatio = imgProps.width / imgProps.height;
        const pdfImgWidth = contentWidth;
        const pdfImgHeight = pdfImgWidth / imgAspectRatio;
        const availableHeight =
          pdfPageHeight - margin * 2 - headerHeight - footerHeight;

        totalPagesCount += Math.ceil(pdfImgHeight / availableHeight);
      }

      for (let i = 0; i < sections.length; i++) {
        const section = sections[i];
        section.style.position = 'absolute';
        section.style.left = '-9999px';
        document.body.appendChild(section);

        await new Promise((resolve) => setTimeout(resolve, 300));

        const canvas = await html2canvas(section, {
          scale: 1.5,
          width: section.scrollWidth,
          height: section.scrollHeight,
          useCORS: true,
          allowTaint: true,
          logging: false,
          windowWidth: 1920,
          backgroundColor: '#ffffff',
        });

        document.body.removeChild(section);

        const imgData = canvas.toDataURL('image/jpeg', 0.8);
        const imgProps = pdf.getImageProperties(imgData);
        const imgAspectRatio = imgProps.width / imgProps.height;
        const pdfImgWidth = contentWidth;
        const pdfImgHeight = pdfImgWidth / imgAspectRatio;
        const availableHeight =
          pdfPageHeight - margin * 2 - headerHeight - footerHeight;

        if (i > 0) {
          pdf.addPage();
          globalPageCount++;
        } else {
          globalPageCount = 1;
        }

        // Aggiungi header e footer
        addHeaderFooter(
          pdf,
          globalPageCount,
          totalPagesCount,
          logoBase64,
          filterText
        );

        // Se la sezione è più alta di una pagina, suddividila
        if (pdfImgHeight > availableHeight) {
          let heightLeft = pdfImgHeight;
          let position = 0;

          pdf.addImage(
            imgData,
            'JPEG',
            margin,
            margin + headerHeight,
            pdfImgWidth,
            pdfImgHeight
          );
          heightLeft -= availableHeight;

          while (heightLeft > 0) {
            position = heightLeft - pdfImgHeight + margin + headerHeight;
            pdf.addPage();
            globalPageCount++;

            // Aggiungi header e footer a ogni pagina
            addHeaderFooter(
              pdf,
              globalPageCount,
              totalPagesCount,
              logoBase64,
              filterText
            );

            pdf.addImage(
              imgData,
              'JPEG',
              margin,
              position,
              pdfImgWidth,
              pdfImgHeight
            );
            heightLeft -= availableHeight;
          }
        } else {
          pdf.addImage(
            imgData,
            'JPEG',
            margin,
            margin + headerHeight,
            pdfImgWidth,
            pdfImgHeight
          );
        }
      }

      if (action === 'download') {
        // file name with current date
        const fileName = `${from}_${
          new Date().toISOString().split('T')[0]
        }.pdf`;
        pdf.save(fileName);
      } else if (action === 'print') {
        pdf.output('dataurlnewwindow');
      }
    } catch (error) {
      console.error('Errore durante la generazione del PDF:', error);
      alert('Errore durante la preparazione del documento.');
    } finally {
      setIsPrinting(false);
    }
  };

  return { isPrinting, setIsPrinting, generatePdf };
};

export default useDashboardPdf;
